import { useEffect, useRef, forwardRef } from 'react';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';

/**
 * DateField component that renders a date picker.
 * When dateGroup metadata is provided (DD/MM/YYYY field names),
 * selecting a date will split the value into individual DD, MM, YYYY fields.
 *
 * @param {Object} props
 * @param {Object} props.field - Field info with name, type, etc.
 * @param {string} props.value - Current value (DD/MM/YYYY format)
 * @param {Function} props.onChange - Callback (fieldName, value)
 * @param {Object} props.style - Position/size styles
 * @param {string} [props.dateGroup] - Comma-separated DD,MM,YYYY field names (e.g., "txtDD1,txtMM1,txtYYYY1")
 */
const DateField = forwardRef(({ field, value, onChange, style, dateGroup }, ref) => {
  const inputRef = useRef(null);
  const fpRef = useRef(null);

  // Auto-scale font size to fit "DD/MM/YYYY" (10 chars) within the field width
  const computedFontSize = (() => {
    if (style.fontSize) return style.fontSize;
    const widthPx = parseFloat(style.width) || 100;
    // Each char ~0.55em wide at most, plus 2px padding total
    const availableWidth = widthPx - 4;
    const maxFontForWidth = availableWidth / (10 * 0.55);
    // Also constrain by height
    const heightPx = parseFloat(style.height) || 20;
    const maxFontForHeight = heightPx * 0.85;
    return `${Math.max(6, Math.min(maxFontForWidth, maxFontForHeight, 14))}px`;
  })();

  useEffect(() => {
    if (inputRef.current && !fpRef.current) {
      fpRef.current = flatpickr(inputRef.current, {
        dateFormat: 'd/m/Y',
        disableMobile: true, // Prevent native mobile picker — it breaks absolute positioning
        static: true, // Render calendar inline relative to the input wrapper
        onChange: (selectedDates, dateStr) => {
          // Set the combined date value on the main field
          onChange(field.name, dateStr);
          
          // If dateGroup is provided, split into DD/MM/YYYY individual fields
          if (dateGroup && selectedDates.length > 0) {
            const parts = dateGroup.split(',');
            if (parts.length === 3) {
              const [ddField, mmField, yyyyField] = parts;
              const date = selectedDates[0];
              const dd = String(date.getDate()).padStart(2, '0');
              const mm = String(date.getMonth() + 1).padStart(2, '0');
              const yyyy = String(date.getFullYear());
              
              onChange(ddField, dd);
              onChange(mmField, mm);
              onChange(yyyyField, yyyy);
              
              console.log(`[DateGroup] Set ${ddField}=${dd}, ${mmField}=${mm}, ${yyyyField}=${yyyy}`);
            }
          }
        }
      });

      // Fix wrapper positioning for all browsers (Chrome, Safari, Firefox)
      const wrapper = inputRef.current?.closest?.('.flatpickr-wrapper');
      if (wrapper) {
        Object.assign(wrapper.style, {
          position: 'absolute',
          top: '0',
          left: '0',
          width: '100%',
          height: '100%'
        });
      }
    }

    return () => {
      if (fpRef.current) {
        fpRef.current.destroy();
        fpRef.current = null;
      }
    };
  }, [field.name, onChange, dateGroup]);

  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        left: style.left,
        top: style.top,
        width: style.width,
        height: style.height,
        zIndex: style.zIndex,
        overflow: 'visible'
      }}
    >
      <input
        ref={inputRef}
        type="text"
        className="pdf-date-field"
        value={value || ''}
        onChange={(e) => onChange(field.name, e.target.value)}
        placeholder="DD/MM/YYYY"
        style={{
          width: '100%',
          height: '100%',
          border: style.border || '1px solid #ccc',
          padding: '0px 1px',
          fontSize: computedFontSize,
          fontFamily: style.fontFamily || "'Times New Roman', Times, serif",
          fontWeight: style.fontWeight || 'normal',
          fontStyle: style.fontStyle || 'normal',
          textDecoration: style.textDecoration || 'none',
          color: style.color || '#000000',
          textAlign: style.textAlign || 'left',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          cursor: 'pointer',
          boxSizing: 'border-box',
          overflow: 'visible'
        }}
      />
    </div>
  );
});

DateField.displayName = 'DateField';

export default DateField;
